import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  Row,
  RowPinningState
} from "@tanstack/react-table";
import { GenericObject } from "../../types";
import { createColumnHelper } from "@tanstack/react-table";
import { createCellFunction } from "../Utilities/CellRendererUtils";
import {
  buildPlaybookColumnLayoutStyles,
  buildTanStackSizingFromColumn,
} from "../Utilities/ColumnLayoutHelper";
import { getParentOnlySortedRowModel } from "../Utilities/RowModelUtils";

interface UseTableStateProps {
  tableData: GenericObject[];
  columnDefinitions: GenericObject[];
  enableSortingRemoval?: boolean;
  expandedControl?: GenericObject;
  sortControl?: GenericObject;
  firstColumnSort?: boolean;
  onRowToggleClick?: (arg: Row<GenericObject>) => void;
  selectableRows?: boolean;
  initialLoadingRowsCount?: number;
  loading?: boolean | string;
  pagination?: boolean;
  paginationProps?: GenericObject;
  pinnedRows?: {
    value?: RowPinningState;
    onChange?: (value: RowPinningState) => void;
  };
  virtualizedRows?: boolean;
  tableOptions?: GenericObject;
  onRowSelectionChange?: (arg: RowSelectionState) => void;
  columnVisibilityControl?: GenericObject;
  rowStyling?: GenericObject[];
  inlineRowLoading?: boolean;
  sortParentOnly?: boolean;
  fullWidthCell?: boolean;
}

export function useTableState({
  tableData,
  columnDefinitions,
  enableSortingRemoval,
  expandedControl,
  sortControl,
  onRowToggleClick,
  selectableRows,
  initialLoadingRowsCount = 10,
  loading,
  pagination = false,
  paginationProps,
  virtualizedRows = false,
  tableOptions,
  columnVisibilityControl,
  pinnedRows,
  rowStyling,
  inlineRowLoading = false,
  sortParentOnly = false,
  fullWidthCell = false,
}: UseTableStateProps) {

  // Create a local state for expanded and setExpanded if expandedControl not used
  const [localExpanded, setLocalExpanded] = useState({});
  const [loadingStateRowCount, setLoadingStateRowCount] = useState(initialLoadingRowsCount);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [localColumnVisibility, setLocalColumnVisibility] = useState({});
  const [localRowPinning, setLocalRowPinning] = useState<RowPinningState>({
    top: [],
    bottom: [],
  });
  // Manage local state to preserve current page index when inlineRowLoading is enabled so it does not boot table to page 1
  // We can extend this for more usecases, but for now scoping it only for inlineRowLoading
  const [localPagination, setLocalPagination] = useState({
    pageIndex: paginationProps?.pageIndex ?? 0, 
    pageSize: paginationProps?.pageSize ?? 20,
  });
  // Determine whether to use the prop or the local state
  const expanded = expandedControl ? expandedControl.value : localExpanded;
  const setExpanded = expandedControl ? expandedControl.onChange : setLocalExpanded;
  const columnVisibility = (columnVisibilityControl && columnVisibilityControl.value) ? columnVisibilityControl.value : localColumnVisibility;
  const setColumnVisibility = (columnVisibilityControl && columnVisibilityControl.onChange) ? columnVisibilityControl.onChange : setLocalColumnVisibility;
  const rowPinning = pinnedRows?.value ?? localRowPinning
  const onRowPinningChange = pinnedRows?.onChange ?? setLocalRowPinning

  // Virtualized data handling (chunked loading)
  const fetchSize = 20; // Number of rows per "page"
  const [fullData] = useState(tableData); // All data from the JSON file
  const [dataChunk, setDataChunk] = useState(fullData.slice(0, fetchSize)); // Initial chunk
  const [totalFetched, setTotalFetched] = useState(fetchSize); // Track loaded rows
  const [isFetching, setIsFetching] = useState(false);

  // Create column structure
  const columnHelper = createColumnHelper();

  // Build columns with proper cell renderers
  const buildColumns = useCallback((columnDefinitions: GenericObject[], isRoot = true): any[] => {
    return columnDefinitions?.map((column, index) => {
      const isFirstColumn = isRoot && index === 0;
      // Handle grouped columns
      if (column.columns && column.columns.length > 0) {
        return {
          header: column.header ?? column.label ?? "",
          id: column.id ?? column.label ?? `group-${index}`,
          columns: buildColumns(column.columns, false),
        };
      }
      const tanStackSizing = buildTanStackSizingFromColumn(column);
      const layoutStyles = buildPlaybookColumnLayoutStyles(column, tanStackSizing);
      const userMeta =
        column.meta &&
        typeof column.meta === "object" &&
        !Array.isArray(column.meta)
          ? column.meta
          : {};
      const hasLayoutStyles =
        layoutStyles.width !== undefined ||
        layoutStyles.minWidth !== undefined ||
        layoutStyles.maxWidth !== undefined;

      // Define the base column structure
      const columnStructure = {
        ...columnHelper.accessor(column.accessor, {
          header: column.header ?? column.label ?? "",
          enableSorting: isFirstColumn || column.enableSort === true,
          ...tanStackSizing,
          meta: {
            ...userMeta,
            ...(hasLayoutStyles
              ? { playbookColumnLayoutStyles: layoutStyles }
              : {}),
          },
        }),
      };

      if (column.cellAccessors || column.customRenderer) {
        columnStructure.cell = createCellFunction(
          column.cellAccessors || [],
          column.customRenderer,
          isFirstColumn,
          onRowToggleClick,
          selectableRows,
          rowStyling,
          fullWidthCell
        );
      }

      return columnStructure;
    }) || [];
  }, [columnHelper, onRowToggleClick, rowStyling, selectableRows, fullWidthCell]);

  const columns = useMemo(() => buildColumns(columnDefinitions), [buildColumns, columnDefinitions]);

  // Sorting configuration
  const sorting = useMemo(() => ([{
    id: columnDefinitions[0].accessor,
    desc: sortControl && sortControl.value !== null ? !sortControl.value.desc : false,
  }]), [columnDefinitions, sortControl]);

  // Custom state based on features enabled
  const customState = useCallback(() => ({
    state: {
      expanded,
      ...(sortControl     && { sorting }),
      ...(selectableRows  && { rowSelection }),
      ...(columnVisibility && { columnVisibility }),
      ...(pinnedRows && { rowPinning }),
      ...(inlineRowLoading && { pagination: localPagination }),
    },
  }), [
    expanded,
    sortControl,
    sorting,
    selectableRows,
    rowSelection,
    columnVisibility,
    rowPinning,
    inlineRowLoading,
    localPagination
  ]);

  // Pagination configuration
  const paginationInitializer = useMemo(() => {
    if (!pagination) return {};

    if (inlineRowLoading) {
      // Use manual pagination state when inlineRowLoading is enabled (see https://tanstack.com/table/latest/docs/guide/pagination#client-side-pagination)
      return {
        getPaginationRowModel: getPaginationRowModel(),
        paginateExpandedRows: false,
        onPaginationChange: setLocalPagination,
        autoResetPageIndex: false, // This is what prevent auto-reset of page index
      };
    } else {
      // Use normal pagination initialization for non- inlineRowLoading usecases
      return {
        getPaginationRowModel: getPaginationRowModel(),
        paginateExpandedRows: false,
        initialState: {
          pagination: {
            pageIndex: paginationProps?.pageIndex ?? 0,
            pageSize: paginationProps?.pageSize ?? 20,
          },
        },
      };
    }
  }, [pagination, paginationProps, inlineRowLoading]);

  // Initialize the table
  const table = useReactTable({
    data: loading ? Array(loadingStateRowCount).fill({}) : (virtualizedRows ? dataChunk : tableData),
    columns,
    onExpandedChange: setExpanded,
    getSubRows: (row: GenericObject) => row.children,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: sortParentOnly ? getParentOnlySortedRowModel() : getSortedRowModel(),
    enableSortingRemoval: enableSortingRemoval,
    sortDescFirst: true,
    onRowSelectionChange: setRowSelection,
    onRowPinningChange,
    getRowId: (selectableRows || pinnedRows || rowStyling) ? row => row.id : undefined,
    onColumnVisibilityChange: setColumnVisibility,
    meta: {
      columnDefinitions
    },
    ...customState(),
    ...paginationInitializer,
    ...tableOptions,
  });

  // Handle row pinning changes
    useEffect(() => {
    const topPins = pinnedRows?.value?.top ?? [];
    const bottomPins = pinnedRows?.value?.bottom ?? [];
    
    const rows = table.getRowModel().rows;
    const collectAllDescendantIds = (subs: Row<GenericObject>[]): string[] =>
      subs.flatMap(r => [r.id, ...collectAllDescendantIds(r.subRows)]);
    const expandPins = (ids: string[]): string[] => {
      const allPinned: string[] = [];
      ids.forEach(id => {
        const parent = rows.find(r => r.id === id && r.depth === 0);
        if (parent) {
          allPinned.push(parent.id, ...collectAllDescendantIds(parent.subRows));
        }
      });
      return allPinned;
    };
    onRowPinningChange({
      top: expandPins(topPins),
      bottom: expandPins(bottomPins),
    });
  }, [table, pinnedRows?.value?.top?.join(','), pinnedRows?.value?.bottom?.join(',')]);

  // Set pagination state when pagination is enabled
  useEffect(() => {
    if (pagination && paginationProps?.pageSize) {
      if (inlineRowLoading) {
        // Update local pagination state for inlineRowLoading
        setLocalPagination(prev => ({
          ...prev,
          pageSize: paginationProps.pageSize
        }));
      } else {
        table.setPageSize(paginationProps.pageSize);
      }
    }
  }, [pagination, paginationProps?.pageSize, table, inlineRowLoading]);

  // Update local pagination when paginationProps change and inlineRowLoading is enabled
  useEffect(() => {
    if (pagination && inlineRowLoading && paginationProps) {
      setLocalPagination({
        pageIndex: paginationProps.pageIndex ?? localPagination.pageIndex,
        pageSize: paginationProps.pageSize ?? localPagination.pageSize,
      });
    }
  }, [pagination, inlineRowLoading, paginationProps?.pageIndex, paginationProps?.pageSize]);

  // Call the callback with the new page index when localPagination.pageIndex changes (with inlineRowLoading)
  useEffect(() => {
    if (pagination && inlineRowLoading && paginationProps?.onPageChange) {
      paginationProps.onPageChange(localPagination.pageIndex);
    }
  }, [localPagination.pageIndex, pagination, inlineRowLoading, paginationProps]);

  // Call the callback with the new page index when localPagination.pageIndex changes (without inlineRowLoading)
  useEffect(() => {
    if (pagination && !inlineRowLoading && paginationProps?.onPageChange) {
      const currentPageIndex = table.getState().pagination.pageIndex;
      paginationProps.onPageChange(currentPageIndex);
    }
  }, [table.getState().pagination.pageIndex, pagination, inlineRowLoading, paginationProps]);

  // Check if table has any sub-rows
  const hasAnySubRows = table.getRowModel().rows.some(row => row.subRows && row.subRows.length > 0);
  const selectedRowsLength = Object.keys(table.getState().rowSelection).length;

  // Data fetching for virtualized view
  const fetchNextPage = useCallback(() => {
    if (isFetching || totalFetched >= fullData.length) return;

    setIsFetching(true);
    // Simulate API call delay
    setTimeout(() => {
      const nextChunk = fullData.slice(totalFetched, totalFetched + fetchSize);
      setDataChunk(prev => [...prev, ...nextChunk]);
      setTotalFetched(prev => prev + nextChunk.length);
      setIsFetching(false);
    }, 500);
  }, [isFetching, totalFetched, fullData, fetchSize]);

  // Update row count for loading state
  const updateLoadingStateRowCount = useCallback(() => {
    const rowsCount = table.getRowModel().rows.length;
    if (rowsCount !== loadingStateRowCount && rowsCount !== 0) {
      setLoadingStateRowCount(rowsCount);
    }
  }, [loadingStateRowCount, table]);

  return {
    table,
    expanded,
    setExpanded,
    hasAnySubRows,
    selectedRowsLength,
    fetchNextPage,
    updateLoadingStateRowCount,
    rowSelection,
    fullData,
    totalFetched,
    isFetching,
    localPagination,
    setLocalPagination
  };
}
